import React, { FC, MouseEvent } from 'react';
import { OverflowCountChip } from '@components/AppliedFilterChip';
import { Popover } from '@mui/material';
import { ModelFilterCategory } from '@store/zustand/model-filters';
import { CATEGORY_ICONS } from '@views/Project/components/ModelFiltersPopover/categoryIcons';
import FilterCategoryRow from '@views/Project/components/ModelFiltersPopover/components/FilterCategoryRow';
import {
  useFiltersPopover,
  useFiltersPopoverActions,
} from '@views/Project/components/ModelFiltersPopover/filtersPopover.store';
import { AppliedFilterChipModel } from '@views/Project/components/Toolbox/components/AppliedFilterChips/useAppliedFilterChips';
import {
  bindPopover,
  bindTrigger,
  usePopupState,
} from 'material-ui-popup-state/hooks';

type Props = {
  hidden: AppliedFilterChipModel[];
};

/**
 * The +N chip and the popover it opens, listing only the applied categories
 * the toolbar could not fit, in category order.
 *
 * Each row is the Filters root panel's own category row — icon, name,
 * summary, dot and chevron — fed the chip model's values and labels, so its
 * summary is the same call the chip's text came from. Choosing a row closes
 * this popover and opens the Filters popover on that category, anchored to
 * the +N chip rather than to a chip that is not on screen; opening this one
 * closes the Filters popover first, so the two never show at once. The paper
 * hangs from the chip's right edge, as the Figma overflow frame draws it: +N
 * exists only when the row is full, so it always sits at the row's right end,
 * where a 340px paper opening rightwards would run past the kebab. Unlike the
 * Filters popover this one may be the Modal-backed `Popover`, because nothing
 * requires a click outside it to reach the element it lands on. Only the click
 * handler wraps the trigger's: popup-state defers a touch-start open to the
 * click that follows it, so the click is where the popover actually opens.
 *
 * +N wears the active ring while either popover is showing something of its
 * own: its list, or the Filters popover on one of the categories it holds,
 * whose own chip is by definition not on the row to wear it.
 */
const HiddenFiltersPopover: FC<Props> = ({ hidden }) => {
  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'hidden-filters',
  });
  const { openCategory, close: closeFilters } = useFiltersPopoverActions();
  const { anchorEl, category } = useFiltersPopover();
  const trigger = bindTrigger(popupState);

  const isActive =
    popupState.isOpen ||
    (Boolean(anchorEl) && hidden.some((chip) => chip.category === category));

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    closeFilters();
    trigger.onClick(event);
  };

  const handleSelect = (category: ModelFilterCategory) => {
    const { anchorEl } = popupState;

    popupState.close();

    if (anchorEl instanceof HTMLElement) {
      openCategory(category, anchorEl);
    }
  };

  return (
    <>
      <OverflowCountChip
        count={hidden.length}
        className={isActive ? 'selected' : undefined}
        {...trigger}
        onClick={handleOpen}
      />

      <Popover
        {...bindPopover(popupState)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 340, mt: 2 } } }}
      >
        {hidden.map((chip) => (
          <FilterCategoryRow
            key={chip.category}
            icon={CATEGORY_ICONS[chip.category]}
            label={chip.label}
            values={chip.values}
            labels={chip.labels}
            onClick={() => handleSelect(chip.category)}
          />
        ))}
      </Popover>
    </>
  );
};

export default HiddenFiltersPopover;
